/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { User } from '../../models/user.model';
import { EnvironmentService } from '../environment/environment.service';

@Injectable()
export class AuthenticationService {

  /** Observable which will return the user's information from the token */
  get userInfo(): User {
    const claims = this.oAuthService.getIdentityClaims();
    return this.constructUserFromIdentityClaims(claims);
  }

  /**
   * If user exists, it returns authentication ID token from oAuthService
   * as an HTTP bearer Token header value.
   */
  getAuthorizationHeaderValue(): string {
    return this.userInfo == null ? '' : `Bearer ${this.oAuthService.getIdToken()}`;
  }

  constructor(private readonly environmentService: EnvironmentService,
              private readonly oAuthService: OAuthService) { }

  async initialize(): Promise<boolean> {
    this.oAuthService.configure(this.environmentService.environment.auth);
    return this.oAuthService.loadDiscoveryDocumentAndLogin();
  }

  /**
   * Will setup up silent refreshing for when the token is about to expire.
   * When the user is logged out via this.logOut method, the silent refreshing will pause and not refresh the tokens
   * until the user is logged back in via receiving a new token.
   */
  enableSilentRenew(): void {
    this.oAuthService.setupAutomaticSilentRefresh();
  }

  /**
   * Removes all tokens and logs the user out. If a logout url is configured,
   * the user is redirected to it.
   */
  logout(): void {
    this.oAuthService.logOut();
  }

  /**
   * Creates a FabUser from the claims object passed in.
   * @param claims Object consisting of the user's properties
   * @private
   */
  private constructUserFromIdentityClaims(claims: any): User {
    let user: User;
    if (claims) {
      user = new User(
        claims.uid || '',
        '',
        '',
        claims.gmPreferredName || '',
        claims.sn || '',
        claims.name || '',
        [],
        new Date()
      );
    }
    return user;
  }
}
